import { useEffect, useRef } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { getMyAccountFlagsFn, registerCurrentDevice } from "@/lib/device.functions";
import { getOrCreateDeviceId, parseDeviceInfo } from "@/lib/device-id";

const EPOCH_KEY = "lumvio_session_epoch";
const DEVICE_REGISTERED_KEY = "lumvio_device_registered_for";

/**
 * Runs inside the authenticated layout.
 * - Reads the server's account flags on mount + every 45s.
 * - If the account was banned or force-logged-out (epoch bumped),
 *   signs out and redirects to /banned or /auth.
 */
export function useAccountGuard(userId: string | null) {
  const navigate = useNavigate();
  const fetchFlags = useServerFn(getMyAccountFlagsFn);
  const registerDevice = useServerFn(registerCurrentDevice);
  const active = useRef(true);

  useEffect(() => {
    active.current = true;
    return () => {
      active.current = false;
    };
  }, []);

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;

    async function check() {
      try {
        const flags = await fetchFlags();
        if (cancelled || !active.current) return;

        if (flags.is_banned) {
          await supabase.auth.signOut();
          navigate({ to: "/banned" });
          return;
        }

        const storedRaw = window.sessionStorage.getItem(EPOCH_KEY);
        const stored = storedRaw ? Number(storedRaw) : null;
        if (stored == null || Number.isNaN(stored)) {
          window.sessionStorage.setItem(EPOCH_KEY, String(flags.session_epoch));
        } else if (flags.session_epoch > stored) {
          window.sessionStorage.removeItem(EPOCH_KEY);
          toast.message("You were signed out by an administrator.");
          await supabase.auth.signOut();
          navigate({ to: "/auth" });
        }
      } catch {
        /* ignore transient errors */
      }
    }

    check();

    // Ensure the current device is registered for this session, even when the
    // user returned via a persisted Supabase session (no OTP flow ran).
    (async () => {
      try {
        const marker = `${DEVICE_REGISTERED_KEY}:${userId}`;
        if (window.sessionStorage.getItem(marker)) return;
        const deviceId = getOrCreateDeviceId();
        const info = parseDeviceInfo();
        const res = await registerDevice({
          data: {
            deviceId,
            deviceName: info.name,
            userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
            location: null,
          },
        });
        if (cancelled || !active.current) return;
        if (res.status === "banned") {
          await supabase.auth.signOut();
          navigate({ to: "/banned" });
          return;
        }
        if (res.status === "device_limit_reached") {
          window.location.href = "/device-limit";
          return;
        }
        window.sessionStorage.setItem(marker, "1");
      } catch {
        /* non-fatal */
      }
    })();

    const id = window.setInterval(check, 45_000);
    const onFocus = () => check();
    window.addEventListener("focus", onFocus);
    return () => {
      cancelled = true;
      window.clearInterval(id);
      window.removeEventListener("focus", onFocus);
    };
  }, [userId, fetchFlags, registerDevice, navigate]);
}
